import {
  call, put, select, takeEvery, all,
} from 'redux-saga/effects';
import callAdapter, { callAdapterResponseError } from '@/utils/sagaHelper';
import {
  createOrderByParams,
  findWithCodes,
  findWithReadStatus,
  successAndErrorsOnDestroy,
  wildcardFilter,
} from '@/utils/resultsHelper';
import { getUppyInstance } from '@/utils/uppy';
import { states } from '@/constants/lifecycle';
import { collectionLogoEndpoint } from '@/constants/urls';
import { CARDS_PAGE_SIZE, presentationOptions } from '@/constants/states';
import selectors from '@/selectors';
import { fetchMetrics } from '@/metrics/actions';
import CollectionsService from '../service';
import {
  showCollectionsResults,
  showCollectionsError,
  showCollection,
  showCollectionError,
  newCollection,
  newCollectionError,
  updateCollectionSuccess,
  updateCollectionError,
  destroyCollection,
  destroyCollectionError,
} from '../actions';
import actions, { collectionLists } from '../constants';

export function* collections(action) {
  const items = yield select(selectors.collections);
  const {
    display, filter, pageSize, currentPage, orderBy,
  } = items[action.category];

  const page = action.page || currentPage;
  const size = action.pageSize || pageSize;
  const orderByParams = createOrderByParams(orderBy);
  const { response, error } = yield call(
    callAdapter,
    CollectionsService.collections(
      wildcardFilter(filter),
      page,
      display === presentationOptions.CARDS ? CARDS_PAGE_SIZE : size,
      orderByParams,
    ),
  );

  if (error) {
    yield put(showCollectionsError(action.category, error));
  } else {
    yield put(showCollectionsResults(
      action.category,
      action.append,
      size,
      response.items,
      response.paging.total,
      response.paging.pages,
      response.paging.page,
    ));
  }
}

export function* collection(action) {
  const newResult = {
    code: action.code,
    created_at: Date.now(),
    updated_at: Date.now(),
  };

  if (action.isNew) {
    yield put(showCollection(action.category, action.code, newResult, true));
  } else {
    const { response, error } = yield call(callAdapter, CollectionsService.collection(action.code));
    if (error) {
      yield put(showCollectionError(action.category, action.code, error));
    } else {
      yield put(showCollection(action.category, action.code, response, false));
    }
  }
}

export function* create() {
  const item = yield select(selectors.activeCollection);
  const { response, error } = yield call(callAdapter, CollectionsService.create(item));
  if (error) {
    yield put(newCollectionError(error));
  } else {
    const items = yield select(selectors.collections);
    const toRefresh = findWithReadStatus(items, collectionLists, states.SUCCESS);

    yield put(newCollection('open', item.uuid, response));
    yield all(toRefresh.map((category) => collections({ category })));
    yield put(fetchMetrics(['collections']));
  }
}

export function* update(action) {
  const { code, properties } = action;

  const { response, error } = yield call(callAdapter, CollectionsService.update(code, properties));
  if (error) {
    yield put(updateCollectionError('open', action.code, error));
  } else {
    yield put(updateCollectionSuccess(action.code, response));
    yield put(fetchMetrics(['collections']));
  }
}

export function* destroy(action) {
  const results = yield all(action.items.map((i) => callAdapterResponseError(i, CollectionsService.destroy(i))));
  const { success, errors } = successAndErrorsOnDestroy(results, 'Collection not found');

  if (errors.length > 0) {
    yield put(destroyCollectionError(errors));
  }
  if (success.length > 0) {
    const items = yield select(selectors.collections);
    const codes = success.map((item) => item.code);
    const categories = findWithCodes(items, collectionLists, codes);
    const toRefresh = findWithReadStatus(items, categories, states.SUCCESS);

    yield put(destroyCollection(success, false));
    if (toRefresh) {
      yield all(toRefresh.map((category) => collections({ category })));
    }
    yield put(fetchMetrics(['collections']));
  }
}

function uploadFile(category, code, uppy) {
  if (!uppy) {
    return;
  }

  const files = uppy.getFiles();
  if (!files.length) {
    return;
  }

  files.forEach((file) => {
    const uppyFile = uppy.getFile(file.id);
    uppy.setFileState(file.id, {
      xhrUpload: {
        ...uppyFile.xhrUpload,
        endpoint: collectionLogoEndpoint(code),
      },
    });
  });

  uppy.upload().then(() => {
    uppy.cancelAll();
  });
}

export function* checkSaveImage({ category, code, uppyId }) {
  const uppy = getUppyInstance(uppyId);
  yield call(uploadFile, category, code, uppy);
}

function resetFiles(uppy) {
  if (uppy) { uppy.cancelAll(); }
}

export function* resetImage({ uppyId }) {
  const uppy = getUppyInstance(uppyId);
  yield call(resetFiles, uppy);
}

export function* refreshCollectionsPresentation(action) {
  yield call(collections, { ...action, page: 1, append: false });
}

export default function* collectionsApi() {
  yield takeEvery(
    [
      actions.FETCH_COLLECTIONS,
      actions.REFRESH_COLLECTIONS,
      actions.TOGGLE_COLLECTIONS_COLUMN_ORDER_BY,
      actions.TOGGLE_COLLECTIONS_SINGLE_SORTING,
    ],
    collections,
  );
  yield takeEvery(actions.FETCH_COLLECTION, collection);
  yield takeEvery(actions.CREATE_COLLECTION, create);
  yield takeEvery(actions.UPDATE_COLLECTION, update);
  yield takeEvery(actions.DELETE_COLLECTION, destroy);
  yield takeEvery(actions.CHECK_COLLECTION_IMAGE, checkSaveImage);
  yield takeEvery(actions.RESET_COLLECTION_IMAGE, resetImage);
  yield takeEvery(actions.TOGGLE_COLLECTIONS_PRESENTATION, refreshCollectionsPresentation);
}
